import * as React from "react"
import { AlertCircle } from "lucide-react"
import {
  ConfigPropertiesPanel,
  type ConfigProperty,
  type ConfigPropertyValue,
  type EnumOption,
} from "@/components/alfred/config-properties-panel"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Button } from "@/components/ui/button"
import { useToast } from "@/hooks/use-toast"
import {
  OPERATING_MODE_OPTIONS,
  type AlfredGlobalConfig,
  type AlfredGlobalConfigKey,
  type AppleVendor,
  type DisplayConfigurationItem,
  type GoogleVendor,
  type ProVenueInstance,
  type ProVenueInstanceInput,
  type UpdateAlfredGlobalConfigRequest,
} from "@/types/alfred-config"
import {
  useAlfredGlobalConfig,
  useUpdateAlfredGlobalConfig,
} from "@/hooks/use-alfred-config"
import {
  ADMIN_IDLE_OPTIONS,
  DISPLAY_TIME_OPTIONS,
  OFFLINE_BATCH_OPTIONS,
  SOUND_INVALID_OPTIONS,
  SOUND_VALID_OPTIONS,
  SOUND_WARNING_OPTIONS,
  TURNSTILE_MODE_OPTIONS,
  UNLOCKED_ACCESS_OPTIONS,
  VALIDATION_VENDOR_OPTIONS,
} from "@/components/alfred/alfred-device-form-fields"
import { AppleVendorEditor } from "@/components/alfred/apple-vendor-editor"
import { DisplayConfigurationEditor } from "@/components/alfred/display-configuration-editor"
import { GoogleVendorEditor } from "@/components/alfred/google-vendor-editor"
import { ProVenueInstanceEditor } from "@/components/alfred/pro-venue-instance-editor"
import { presentAlfredConfigFields } from "@/components/alfred/alfred-device-field-presentation"

// Field descriptors own field behavior; the shared presentation contract owns
// visible labels, sections, and render order across global and device views.
type FieldDescriptor =
  | {
      // ticketmasterConsumerKey is write-only and therefore absent from the GET
      // type, but it is still an editable masked credential on this form.
      key: AlfredGlobalConfigKey | "ticketmasterConsumerKey"
      type: "string"
      password?: boolean
      maskedCredential?: boolean
      placeholder?: string
      helperText?: string
    }
  | { key: AlfredGlobalConfigKey; type: "number"; min?: number; max?: number; step?: number }
  | { key: AlfredGlobalConfigKey; type: "boolean" }
  | { key: AlfredGlobalConfigKey; type: "string-array" }
  | {
      key: AlfredGlobalConfigKey
      type: "json"
      itemLabel?: string
      editor?: (
        value: unknown[],
        onChange: (next: unknown[]) => void,
        disabled: boolean,
        accessibleLabel: string,
      ) => React.ReactNode
    }
  | {
      key: AlfredGlobalConfigKey
      type: "enum"
      options: ReadonlyArray<EnumOption>
      numeric?: boolean
    }

const BASE_FIELD_DESCRIPTORS: FieldDescriptor[] = [
  { key: "validationVendor", type: "enum", options: VALIDATION_VENDOR_OPTIONS },
  { key: "proVenueUser", type: "string" },
  {
    key: "proVenueInstances",
    type: "json",
    itemLabel: "instance",
    editor: (value, onChange, disabled, accessibleLabel) => (
      <ProVenueInstanceEditor
        items={value as ProVenueInstanceInput[]}
        onChange={(next) => onChange(next as unknown[])}
        disabled={disabled}
        triggerAriaLabel={accessibleLabel}
      />
    ),
  },
  { key: "facilityCode", type: "string" },
  { key: "league", type: "string" },
  { key: "areaCode", type: "string" },
  { key: "deviceCode", type: "string" },
  { key: "offlineValidationMode", type: "boolean" },
  { key: "offlineBatchUploadLimit", type: "enum", options: OFFLINE_BATCH_OPTIONS, numeric: true },
  { key: "soundValidNumber", type: "enum", options: SOUND_VALID_OPTIONS, numeric: true },
  { key: "soundInvalidNumber", type: "enum", options: SOUND_INVALID_OPTIONS, numeric: true },
  { key: "soundWarningNumber", type: "enum", options: SOUND_WARNING_OPTIONS, numeric: true },
  { key: "maxCpuTemperature", type: "number" },
  { key: "minCpuTemperature", type: "number" },
  {
    key: "configurationPassword",
    type: "string",
    password: true,
    placeholder: "Leave blank to keep current",
    helperText: "Leave blank to keep current. To change it, enter a new password.",
  },
  { key: "exitScanningAllowed", type: "boolean" },
  { key: "adminModeIdleWindow", type: "enum", options: ADMIN_IDLE_OPTIONS, numeric: true },
  { key: "validDisplayTime", type: "enum", options: DISPLAY_TIME_OPTIONS, numeric: true },
  { key: "invalidDisplayTime", type: "enum", options: DISPLAY_TIME_OPTIONS, numeric: true },
  { key: "operatingMode", type: "enum", options: OPERATING_MODE_OPTIONS },
  { key: "turnstileMode", type: "enum", options: TURNSTILE_MODE_OPTIONS, numeric: true },
  { key: "unlockedAccessTime", type: "enum", options: UNLOCKED_ACCESS_OPTIONS, numeric: true },
  { key: "isLockFailSafe", type: "boolean" },
  { key: "useSimpleResponse", type: "boolean" },
  { key: "mainBackgroundColor", type: "string" },
  { key: "secondaryBackgroundColor", type: "string" },
  { key: "idleStateColor", type: "string" },
  { key: "nfcEnabled", type: "boolean" },
  { key: "enableFan", type: "boolean" },
  { key: "enableHotspot", type: "boolean" },
  { key: "wakeLockEnabled", type: "boolean" },
  { key: "enableScanReset", type: "boolean" },
  { key: "enableManualValidationMask", type: "boolean" },
  {
    key: "displayConfiguration",
    type: "json",
    itemLabel: "screen",
    editor: (value, onChange, disabled, accessibleLabel) => (
      <DisplayConfigurationEditor
        items={value as DisplayConfigurationItem[]}
        onChange={(next) => onChange(next as unknown[])}
        disabled={disabled}
        triggerAriaLabel={accessibleLabel}
      />
    ),
  },
  {
    key: "appleVendors",
    type: "json",
    itemLabel: "credential",
    editor: (value, onChange, disabled, accessibleLabel) => (
      <AppleVendorEditor
        items={value as AppleVendor[]}
        onChange={(next) => onChange(next as unknown[])}
        disabled={disabled}
        triggerAriaLabel={accessibleLabel}
      />
    ),
  },
  {
    key: "googleVendors",
    type: "json",
    itemLabel: "credential",
    editor: (value, onChange, disabled, accessibleLabel) => (
      <GoogleVendorEditor
        items={value as GoogleVendor[]}
        onChange={(next) => onChange(next as unknown[])}
        disabled={disabled}
        triggerAriaLabel={accessibleLabel}
      />
    ),
  },
  { key: "ticketmasterVenueId", type: "string" },
  {
    key: "ticketmasterConsumerKey",
    type: "string",
    maskedCredential: true,
    placeholder: "Leave blank to keep current",
    helperText: "Leave blank to keep current. To change it, enter a new consumer key.",
  },
]

const FIELD_DESCRIPTORS = presentAlfredConfigFields(BASE_FIELD_DESCRIPTORS)

function configToProperties(config: AlfredGlobalConfig): ConfigProperty[] {
  return FIELD_DESCRIPTORS.map((field): ConfigProperty => {
    const raw = (config as unknown as Record<string, unknown>)[field.key]
    switch (field.type) {
      case "string":
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "string",
          // GET intentionally omits write-only secrets (configurationPassword,
          // ticketmasterConsumerKey); credential inputs always render empty so
          // a save without a new value keeps the current secret.
          value:
            field.password || field.maskedCredential
              ? ""
              : ((raw as string | undefined) ?? ""),
          password: field.password,
          maskedCredential: field.maskedCredential,
          placeholder: field.placeholder,
          helperText: field.helperText,
        }
      case "number":
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "number",
          value: raw as number,
          min: field.min,
          max: field.max,
          step: field.step,
        }
      case "boolean":
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "boolean",
          value: raw as boolean,
        }
      case "string-array":
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "string-array",
          value: raw as unknown as string[],
        }
      case "json": {
        // ProVenue instances arrive with only non-sensitive metadata. Seed the
        // editor's working value with blank write-only credentials so entered
        // secrets persist across sheet reopens but are never sourced from the API.
        const jsonValue =
          field.key === "proVenueInstances"
            ? ((raw as ProVenueInstance[] | undefined) ?? []).map((instance) => ({
                ...instance,
                consumerKey: "",
                consumerSecret: "",
              }))
            : (raw as unknown[])
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "json",
          value: jsonValue as unknown[],
          itemLabel: field.itemLabel,
          editor: field.editor,
        }
      }
      case "enum":
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "enum",
          value: raw as string | number,
          options: field.options,
        }
    }
  })
}

function propertiesToUpdate(
  next: Record<string, ConfigPropertyValue | null>,
): UpdateAlfredGlobalConfigRequest {
  const update: Record<string, ConfigPropertyValue> = {}
  for (const field of FIELD_DESCRIPTORS) {
    const v = next[field.key]
    if (v === undefined || v === null) continue
    // Credential fields are rotate-only: an empty submit means "leave the
    // existing value alone", so omit it from the payload entirely.
    if (
      field.type === "string" &&
      (field.password || field.maskedCredential) &&
      v === ""
    ) continue
    // Number fields may be cleared while editing; omit until populated.
    if (field.type === "number" && v === "") continue
    // Enum dropdowns whose backing value is an integer round-trip through the
    // Radix Select as strings; coerce back so the API receives a number.
    if (field.type === "enum" && field.numeric && typeof v === "string") {
      update[field.key] = Number(v)
      continue
    }
    update[field.key] = v
  }
  return update as UpdateAlfredGlobalConfigRequest
}

export function GlobalDefaultsView() {
  const { toast } = useToast()
  const { data: config, error, isLoading, mutate } = useAlfredGlobalConfig()
  const update = useUpdateAlfredGlobalConfig()

  if (isLoading) {
    return <PropertiesSkeleton />
  }

  if (error || !config) {
    return <LoadErrorState onRetry={() => mutate()} />
  }

  const properties = configToProperties(config)

  const handleSave = async (next: Record<string, ConfigPropertyValue | null>) => {
    try {
      const patch = propertiesToUpdate(next)
      // PATCH and use the server's response to repopulate SWR's cache so any
      // other consumer (or this view's next render) sees the canonical state
      // without an extra GET round-trip.
      await update.trigger(patch, {
        populateCache: true,
        revalidate: false,
      })
      toast({
        variant: "success",
        title: "Global defaults updated",
        description: "Values will be inherited by devices without overrides.",
      })
    } catch (err) {
      toast({
        variant: "error",
        title: "Failed to update global defaults",
        description: err instanceof Error ? err.message : "Please try again.",
      })
      // Re-throw so the panel keeps the user in edit mode with their pending
      // changes intact, letting them retry without re-entering everything.
      throw err
    }
  }

  return (
    <ConfigPropertiesPanel
      properties={properties}
      resetKey={config}
      onSave={handleSave}
      isSaving={update.isMutating}
      showUseDefault={false}
      configurationTitle="Global configuration"
      saveConfirmationTitle="Save global configuration changes?"
      saveConfirmationDescription="Are you sure you want to continue? This change will affect all downstream devices."
    />
  )
}

function PropertiesSkeleton() {
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-6 space-y-4">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="flex items-center gap-6">
            <Skeleton className="h-4 w-1/3 bg-muted" />
            <Skeleton className="h-9 flex-1 max-w-xs bg-muted" />
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function LoadErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="flex flex-col items-center justify-center py-16">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 mb-4">
          <AlertCircle className="h-8 w-8 text-destructive" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">
          Failed to load global defaults
        </h3>
        <p className="text-sm text-muted-foreground mb-6 text-center max-w-sm">
          The Alfred global config could not be loaded. Please try again.
        </p>
        <Button
          onClick={onRetry}
          className="bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          Retry
        </Button>
      </CardContent>
    </Card>
  )
}
